import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DecimalPipe, NgClass } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { IconName } from '@app/utils/enums/icon-enum';
import { InventoryItemFullInformation } from '@app/utils/interfaces/inventory-item.interface';
import { AutoSizeDirective } from '@app/pages/inventory/directives/auto-size.directive';
import {
  REQUIRED_ERROR_MESSAGES,
  INVALID_ERROR_MESSAGES,
  PRODUCT_GENERAL_FIELDS,
  AllProductFieldKey,
  ProductFieldKey,
  InvalidKeys,
} from '@app/utils/constants/inventory-table.constant';
import { CategoriesService } from '@app/pages/home-page/shop-settings/settings-categories/services/categories.service';
import { catchError, EMPTY, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Category } from '@app/utils/interfaces/categories.interface';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-product-general-info',
  standalone: true,
  imports: [
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    TranslatePipe,
    DecimalPipe,
    NgClass,
    AutoSizeDirective,
  ],
  templateUrl: './product-general-info.component.html',
  styleUrl: './product-general-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGeneralInfoComponent implements OnInit {
  private readonly requiredMessages = REQUIRED_ERROR_MESSAGES;
  private readonly invalidMessages = INVALID_ERROR_MESSAGES;

  private categoriesService = inject(CategoriesService);
  private destroyRef = inject(DestroyRef);

  public readonly item = input<InventoryItemFullInformation>();
  public readonly isEditable = input<boolean>(false);
  public readonly productDescriptionRows = input<number>(1);
  public readonly showPrice = input<boolean>(true);
  public readonly form = input.required<FormGroup>();
  public readonly IconName = IconName;
  public readonly fields = PRODUCT_GENERAL_FIELDS;
  public readonly categories = signal<Category[]>([]);

  ngOnInit(): void {
    this.categoriesService
      .getCategories({})
      .pipe(
        tap((res) => {
          if (res?.data?.categories) {
            this.categories.set(res.data.categories);
          }
        }),
        catchError(() => {
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public readonly filteredFields = computed(() => {
    const fields = this.fields;
    const showPrice = this.showPrice();

    return showPrice ? fields : fields.filter((field) => field.key !== 'priceUsd');
  });

  public getRequiredMessage(key: AllProductFieldKey): string {
    return this.requiredMessages[key as ProductFieldKey] ?? '';
  }

  public getInvalidMessage(key: AllProductFieldKey): string {
    return this.invalidMessages[key as InvalidKeys] ?? '';
  }
}
